import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import { icon_trash } from '@gn-audio/icons';
import { BasketException, SFCCBasketCoupon } from '@gn-audio/jss-core';

import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { Button } from '../../Button/Button';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { useBasket } from '../BasketContext/useBasket';

import basketStyles from '../Basket.module.css';

export const CouponCodes = () => {
  const { t } = useFallbackTranslation();
  const { applyCoupon, basket, isBasketOpen, removeCoupon } = useBasket();
  const [showPromoCodeForm, setShowPromoCodeForm] = useState<boolean>(false);
  const [promoCode, setPromoCode] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const handlePromoCodeClick = () => {
    setShowPromoCodeForm(true);
  };

  const handleSubmitPromoCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!promoCode) return;
    await applyCoupon(promoCode).catch((error: BasketException) => {
      setErrorMessage(error.message);
    });
    setPromoCode(undefined);
    return false;
  };

  const handleChangePromoCode = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(undefined);
    setPromoCode(e.currentTarget.value);
  };

  useEffect(() => {
    if (!isBasketOpen) {
      setPromoCode(undefined);
      setErrorMessage(undefined);
      setShowPromoCodeForm(false);
    }
  }, [isBasketOpen]);

  const handleRemoveCoupon = (coupon: SFCCBasketCoupon) => {
    removeCoupon(coupon.coupon_item_id);
  };
  return (
    <div className={classNames(basketStyles.card, basketStyles.grey, 'flex flex-col gap-4')}>
      {basket?.coupon_items?.length ? (
        <div className="flex flex-col gap-2">
          <span className="text-[0.75rem]">{t('cart.AppliedCoupon')}</span>
          <ul>
            {basket?.coupon_items?.map((coupon) => {
              return (
                <li key={coupon.coupon_item_id} className="flex items-center justify-between">
                  <span className="text-[1rem] font-bold">{coupon.code}</span>
                  <Button
                    variant="tertiary"
                    size="m"
                    aria-label={t('cart.RemoveCoupon')}
                    icon={<JabraIcon className="size-6" Icon={icon_trash} />}
                    iconPlacement="right"
                    onClick={() => handleRemoveCoupon(coupon)}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <>
          {showPromoCodeForm ? (
            <>
              <form
                className="flex flex-1 flex-wrap justify-between gap-3"
                onSubmit={handleSubmitPromoCode}
              >
                <input
                  autoFocus={true}
                  aria-label="Coupon code"
                  value={promoCode}
                  className="jabra-input h-[3.5rem] pl-5"
                  onChange={handleChangePromoCode}
                  role="text"
                  type="text"
                  placeholder={t('cart.EnterPromoCode')}
                />
                <Button type="submit" variant="primary" size="l" disabled={!isEmpty(errorMessage)}>
                  {t('cart.Apply')}
                </Button>
              </form>
              {!isEmpty(errorMessage) && <p className="text-[red]">{errorMessage}</p>}
            </>
          ) : (
            <button className="h-full self-start" onClick={handlePromoCodeClick}>
              <p>{t('cart.DoYouHavePromoCode')}</p>
            </button>
          )}
        </>
      )}
    </div>
  );
};
